/**
 * A list of options, floating over whatever opened it — `KLQ-20260828-001` §B.3.
 *
 * **`ChipSelect` said when to write this file, and this is that moment.** Its own header:
 * *"If a search field inside the popup is ever genuinely needed, that is the moment to write
 * the listbox — and it will be one component, here, not a second dialect per screen."* The
 * grid needs one: a picker inside a table cell may not change the cell's height, and a native
 * `<select multiple size=5>` is a box with a real height in the normal flow — it pushed the
 * row and widened the column every time somebody chose an assignee. A control that reflows the
 * table underneath the person choosing is the thing the cell editor exists to avoid.
 *
 * So the platform control is traded for a portalled `role="listbox"`, and the price is paid
 * here, once, with the four things a hand-written listbox owes:
 *
 * 1. **`aria-activedescendant`, not roving `tabindex`.** Focus stays in the search box (or on
 *    the list itself when there is no search box) so that typing filters and arrowing moves
 *    the highlight at the same time — which is the whole gesture people arrive with from a
 *    spreadsheet.
 * 2. **The keyboard is complete before the pointer is.** Arrows, Home/End, Enter, Escape and
 *    Tab all mean something; nothing here is reachable by mouse alone.
 * 3. **It closes on a viewport move** rather than following it. A popup that tracks a
 *    scrolling table is a second positioning problem; `MenuPopup` made the same call, and two
 *    popups that disagree about scrolling would be worse than either answer.
 * 4. **It owns no copy.** Every word arrives as a prop, the way `ChipSelect` and `Slicer`
 *    take theirs — this file adds no keys to any catalog.
 *
 * What it deliberately does **not** own: what a pick *means*. Single-select reports the id and
 * lets the caller close; multi-select reports a toggle and stays open until the caller is told
 * the person is done. The grid's replace-semantics (§C.4) live in the cell editor, not here.
 */

import { Check, Search } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styles from "./ListboxPopup.module.css";
import type { Placement } from "./placement";
import { place } from "./placement";

export interface ListboxOption {
  readonly id: string;
  readonly label: string;
  /** Drawn before the label — an avatar, a status dot. Optional, and never the only signal. */
  readonly icon?: ReactNode | undefined;
}

/** Above this many options the search box appears. Below it, filtering is scrolling. */
export const SEARCH_ABOVE = 7;

export interface ListboxPopupProps {
  /** The list's accessible name — already translated. Usually the column's own header. */
  readonly label: string;
  /**
   * What the box hangs off. An element rather than a rect: the popup re-reads it when it
   * measures, so a caller that re-renders does not have to hand over a fresh `DOMRect` and
   * risk the placement loop `MenuPopup` guards against.
   */
  readonly anchor: HTMLElement | null;
  readonly options: readonly ListboxOption[];
  /** The ids held right now. One at most when `multiple` is false. */
  readonly selected: readonly string[];
  readonly multiple: boolean;
  /** The search box's placeholder and accessible name. Absent = never show one. */
  readonly searchLabel?: string | undefined;
  /** What a filter that matches nothing says. */
  readonly emptyLabel: string;
  /** The row that means "nobody / nothing". Single-select only; a multiple list says it by
      holding none, and an empty row there would be a trap. */
  readonly noneLabel?: string | undefined;
  /** Seeded into the search box — the character that opened the picker, so typing a name in
      the grid starts filtering rather than being thrown away. */
  readonly initialQuery?: string | undefined;
  /**
   * A pick — single-select: this is now the value; multiple: toggle it.
   *
   * **`andMove` travels with the pick, and that is not decoration.** A single picker answered
   * with `Enter` both chooses and finishes, and the caller's value is a render behind at that
   * moment — reporting the pick and then calling `onDone` would commit the *previous* value
   * over the new one. So one callback carries both halves, and `onDone` is only ever the
   * finish of something this list has already reported.
   */
  readonly onChoose: (id: string, andMove: "down" | "right" | null) => void;
  /** Finishing without picking: a multiple list that is assembled, a click outside, a scroll. */
  readonly onDone: (andMove: "down" | "right" | null) => void;
  readonly onCancel: () => void;
  /** Drawn under the list — the fan-out count, or the question above the threshold. */
  readonly footer?: ReactNode | undefined;
}

export function ListboxPopup({
  label,
  anchor,
  options,
  selected,
  multiple,
  searchLabel,
  emptyLabel,
  noneLabel,
  initialQuery = "",
  onChoose,
  onDone,
  onCancel,
  footer,
}: ListboxPopupProps) {
  const popup = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState(initialQuery);
  /** Null until measured; the popup is invisible until then, so the first painted frame is
      already in the right place. */
  const [placement, setPlacement] = useState<Placement | null>(null);

  const searchable = searchLabel !== undefined && options.length > SEARCH_ABOVE;

  /** `— none —` is a row like any other, so one index walk covers it and the arrow keys do
      not need to know it is special. Its id is the empty string, which is what a cleared
      cell parses from anyway. */
  const rows: readonly ListboxOption[] = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    const matched =
      needle === ""
        ? options
        : options.filter((one) =>
            one.label.toLocaleLowerCase().includes(needle),
          );
    return !multiple && noneLabel !== undefined && needle === ""
      ? [{ id: "", label: noneLabel }, ...matched]
      : matched;
  }, [options, query, multiple, noneLabel]);

  const [active, setActive] = useState(0);

  // The highlight follows the filter: after typing, "the current row" is the first row that
  // survived, never an index into a list that no longer exists.
  useEffect(() => {
    setActive((current) => (current < rows.length ? current : 0));
  }, [rows.length]);

  useLayoutEffect(() => {
    const box = popup.current?.getBoundingClientRect();
    const rect = anchor?.getBoundingClientRect();
    if (box === undefined || rect === undefined) return;
    const next = place({ kind: "element", rect, align: "start" }, box);
    setPlacement((current) =>
      current !== null && current.top === next.top && current.left === next.left
        ? current
        : next,
    );
  }, [anchor, rows.length, footer]);

  // Focus goes in once the box has been placed. Focusing something nobody can see yet is how
  // a screen reader ends up describing a ghost.
  useEffect(() => {
    if (placement === null) return;
    if (searchable) search.current?.focus();
    else list.current?.focus();
  }, [placement, searchable]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("[data-listbox-popup]") !== null) return;
      // A click on the trigger itself is the trigger's to interpret, not a dismissal here —
      // otherwise closing and reopening race each other on one click.
      if (anchor?.contains(target) === true) return;
      onDone(null);
    };
    const onViewportMove = (): void => {
      onDone(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("scroll", onViewportMove, true);
    window.addEventListener("resize", onViewportMove);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", onViewportMove, true);
      window.removeEventListener("resize", onViewportMove);
    };
  }, [anchor, onDone]);

  /** Keeps the highlighted row in view when the arrows walk past the fold. */
  useEffect(() => {
    list.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const move = (delta: number): void => {
    if (rows.length === 0) return;
    setActive((current) => {
      const next = current + delta;
      // Wraps, the way a short menu does: somebody holding ArrowDown expects to come round.
      return ((next % rows.length) + rows.length) % rows.length;
    });
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    // The table underneath listens for the same keys; while this is open they belong here.
    event.stopPropagation();
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        onCancel();
        return;
      case "ArrowDown":
        event.preventDefault();
        move(1);
        return;
      case "ArrowUp":
        event.preventDefault();
        move(-1);
        return;
      case "Home":
        event.preventDefault();
        setActive(0);
        return;
      case "End":
        event.preventDefault();
        setActive(rows.length - 1);
        return;
      case " ": {
        // Space toggles in a multiple list, and types a space in the search box — so it is
        // only claimed when there is no search box to type into.
        if (!multiple || searchable) return;
        event.preventDefault();
        const row = rows[active];
        if (row !== undefined) onChoose(row.id, null);
        return;
      }
      case "Enter": {
        event.preventDefault();
        const row = rows[active];
        // Enter on a multiple list means *done* once the set is assembled, and *toggle* while
        // it is being assembled — so a highlighted row it does not yet hold is added and the
        // list stays open; anything else finishes and moves down like a spreadsheet.
        if (multiple && row !== undefined && !selected.includes(row.id)) {
          onChoose(row.id, null);
          return;
        }
        // A single picker chooses and finishes in the same act, so the move rides with the
        // pick rather than following it a render later.
        if (!multiple && row !== undefined) onChoose(row.id, "down");
        else onDone("down");
        return;
      }
      case "Tab": {
        event.preventDefault();
        const row = rows[active];
        if (!multiple && row !== undefined) onChoose(row.id, "right");
        else onDone("right");
        return;
      }
      default:
        return;
    }
  };

  const listId = `listbox-${label.replace(/\s+/gu, "-")}`;
  const activeId =
    rows[active] === undefined ? undefined : `${listId}-${String(active)}`;

  return createPortal(
    <div
      ref={popup}
      data-listbox-popup=""
      className={[styles.popup, placement === null ? styles.measuring : ""]
        .filter(Boolean)
        .join(" ")}
      style={
        placement === null
          ? undefined
          : {
              top: placement.top,
              left: placement.left,
              // At least as wide as the cell it came from, so it reads as belonging to it.
              minWidth: anchor?.getBoundingClientRect().width,
            }
      }
      onKeyDown={onKeyDown}
    >
      {searchable ? (
        <div className={styles.search}>
          <Search size={14} strokeWidth={1.5} aria-hidden />
          <input
            ref={search}
            type="text"
            className={styles.searchField}
            role="combobox"
            aria-expanded
            aria-controls={listId}
            aria-activedescendant={activeId}
            aria-label={searchLabel}
            placeholder={searchLabel}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </div>
      ) : null}

      <div
        ref={list}
        id={listId}
        role="listbox"
        aria-label={label}
        aria-multiselectable={multiple ? true : undefined}
        aria-activedescendant={searchable ? undefined : activeId}
        tabIndex={searchable ? -1 : 0}
        className={styles.list}
      >
        {rows.length === 0 ? (
          <p className={styles.empty}>{emptyLabel}</p>
        ) : (
          rows.map((row, index) => {
            const on = selected.includes(row.id);
            return (
              <div
                key={row.id === "" ? " none" : row.id}
                id={`${listId}-${String(index)}`}
                role="option"
                aria-selected={on}
                data-active={index === active}
                className={[
                  styles.option,
                  row.id === "" ? styles.none : "",
                  index === active ? styles.active : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                // Highlight follows the pointer, so hovering and arrowing cannot disagree
                // about which row is the current one.
                onMouseEnter={() => {
                  setActive(index);
                }}
                // `mouseDown` and not `click`: the document-level `pointerdown` above would
                // otherwise see the press first and close the list before the click lands.
                onMouseDown={(event) => {
                  event.preventDefault();
                  onChoose(row.id, null);
                }}
              >
                <span className={styles.mark} aria-hidden>
                  {on ? <Check size={14} strokeWidth={2} /> : null}
                </span>
                {row.icon === undefined ? null : (
                  <span className={styles.glyph} aria-hidden>
                    {row.icon}
                  </span>
                )}
                <span className={styles.label}>{row.label}</span>
              </div>
            );
          })
        )}
      </div>

      {footer === undefined ? null : <div className={styles.footer}>{footer}</div>}
    </div>,
    document.body,
  );
}
